/**
 * deepspace secrets
 *
 * Remote, project-scoped secrets for DeepSpace apps. The remote store is the
 * source of truth; .dev.vars* is only a generated cache used by dev/test/deploy.
 */

import { defineCommand } from 'citty'
import { existsSync, readFileSync, rmSync, writeFileSync } from 'node:fs'
import { writeSecretFileSync } from '../lib/secure-file'
import { join, resolve } from 'node:path'
import { ensureToken } from '../auth'
import { extractCustomDevVars, parseDevVars, removeDevVarsKeys, PLATFORM_URLS } from '../env'
import {
  clearSecretsCacheFile,
  createConfig,
  deleteConfig,
  deleteSecret,
  fetchSecretsValues,
  formatSecretsDownload,
  getSecretPlain,
  appScopedSecretsApiOptions,
  listConfigs,
  listSecrets,
  parseSecretsUpload,
  resolveSecretsLink,
  resolveSecretsTarget,
  secretsTargetMatchesLink,
  uploadSecrets,
  validateConfigName,
  validateProjectName,
  validateSecretName,
  validateSecretValue,
  writeSecretsCacheFile,
  writeWranglerSecretsLink,
  removeWranglerSecretsLink,
  removeWranglerSecretsLinkKeys,
  removeWranglerRequiredSecrets,
  setServerSecretsLink,
  clearServerSecretsLink,
  stripGeneratedSecretsCache,
  SecretsApiError,
  type SecretsTarget,
  type SecretsDownloadFormat,
  updateWranglerRequiredSecrets,
  resolveSecretsAppName,
} from '../lib/secrets'

const DEPLOY_URL = process.env.DEEPSPACE_DEPLOY_URL ?? PLATFORM_URLS.deploy

type CommonArgs = {
  dir?: string
  scope?: string
  project?: string
  config?: string
  env?: string
  json?: boolean
}

type RawArgs = CommonArgs & Record<string, unknown>

function appDir(args: CommonArgs): string {
  const dir = typeof args.dir === 'string' && args.dir
    ? args.dir
    : typeof args.scope === 'string' && args.scope
      ? args.scope
      : '.'
  return resolve(dir)
}

function wranglerEnv(args: CommonArgs): string | undefined {
  return typeof args.env === 'string' && args.env.trim() ? args.env.trim() : undefined
}

function targetFromArgs(args: CommonArgs): { appDir: string; target: SecretsTarget } {
  const dir = appDir(args)
  const target = resolveSecretsTarget(dir, {
    project: typeof args.project === 'string' && args.project ? args.project : undefined,
    config: typeof args.config === 'string' && args.config ? args.config : undefined,
    wranglerEnv: wranglerEnv(args),
  })
  return { appDir: dir, target }
}

function scopedArgs() {
  return {
    dir: { type: 'string', description: 'App directory (default: current directory)', required: false },
    scope: { type: 'string', description: 'Directory scope; Doppler-compatible alias for --dir', required: false },
    env: { type: 'string', alias: 'e', description: 'Advanced: wrangler.toml [env.<name>] cache/link slot', required: false },
    json: { type: 'boolean', description: 'Emit JSON', default: false },
  } as const
}

function commonArgs() {
  return {
    ...scopedArgs(),
    project: { type: 'string', alias: 'p', description: 'Secrets project (default: linked project or wrangler name; does not link)', required: false },
    config: { type: 'string', alias: 'c', description: 'Secrets config (Doppler-compatible; default: linked config or prd)', required: false },
  } as const
}

function setupArgs() {
  return {
    ...scopedArgs(),
    project: { type: 'string', alias: 'p', description: 'Secrets project to link (default: linked project or wrangler name)', required: false },
    config: { type: 'string', alias: 'c', description: 'Secrets config to link (default: linked config or prd)', required: false },
    'no-interactive': { type: 'boolean', description: 'Accepted for Doppler CLI compatibility; DeepSpace setup never prompts', default: false },
    'no-save-token': { type: 'boolean', description: 'Accepted for Doppler CLI compatibility; DeepSpace does not save a token here', default: false },
  } as const
}

async function refreshCache(token: string, dir: string, target: SecretsTarget): Promise<number> {
  const values = await fetchSecretsValues(
    DEPLOY_URL,
    token,
    target.projectName,
    target.configName,
    appScopedSecretsApiOptions(dir, target),
  )
  writeSecretsCacheFile(dir, values, target)
  return Object.keys(values).length
}

async function refreshCacheIfLinked(
  token: string,
  dir: string,
  target: SecretsTarget,
): Promise<number | null> {
  if (!secretsTargetMatchesLink(dir, target)) return null
  return refreshCache(token, dir, target)
}

function writeLinkForTarget(dir: string, target: SecretsTarget, projectOverride: boolean): void {
  writeWranglerSecretsLink(dir, target, { projectOverride })
}

function redeployReminder(target: SecretsTarget): void {
  const envFlag = target.wranglerEnv ? ` --env ${target.wranglerEnv}` : ''
  console.log(`Redeploy for production to receive these secrets: npx deepspace deploy${envFlag}`)
}

function unlinkedTargetNotice(target: SecretsTarget): void {
  const envFlag = target.wranglerEnv ? ` --env ${target.wranglerEnv}` : ''
  console.log(
    `Not linked for dev/test/deploy; run ` +
      `npx deepspace secrets setup --project ${target.projectName} --config ${target.configName}${envFlag}.`,
  )
}

function cacheJson(count: number | null): { linked: boolean; cachedSecrets: number | null } {
  return { linked: count !== null, cachedSecrets: count }
}

function updateRequiredSecretsIfTopLevelLinked(
  dir: string,
  target: SecretsTarget,
  keys: string[],
  opts: { replace?: boolean } = {},
): string[] | null {
  if (target.wranglerEnv || !secretsTargetMatchesLink(dir, target)) return null
  return updateWranglerRequiredSecrets(dir, keys, opts)
}

function removeRequiredSecretsIfTopLevelLinked(
  dir: string,
  target: SecretsTarget,
  keys: string[],
): string[] | null {
  if (target.wranglerEnv || !secretsTargetMatchesLink(dir, target)) return null
  return removeWranglerRequiredSecrets(dir, keys)
}

function requiredSecretsJson(keys: string[] | null): { requiredSecrets?: string[] } {
  return keys ? { requiredSecrets: keys } : {}
}

function requiredSecretsNotice(keys: string[] | null): void {
  if (!keys) return
  console.log(`Updated wrangler.toml detected-secret advisory (${keys.length} name${keys.length === 1 ? '' : 's'}).`)
}

type LinkRemoteCheck =
  | { status: 'ok' }
  | { status: 'created' }
  | { status: 'unchecked'; message: string }

async function ensureLinkedRemoteTarget(dir: string, target: SecretsTarget): Promise<LinkRemoteCheck> {
  let token: string
  try {
    token = await ensureToken()
  } catch (err) {
    return {
      status: 'unchecked',
      message: err instanceof Error ? err.message : String(err),
    }
  }

  try {
    const configs = await listConfigs(
      DEPLOY_URL,
      token,
      target.projectName,
      appScopedSecretsApiOptions(dir, target),
    )
    if (configs.some((config) => config.name === target.configName)) {
      return { status: 'ok' }
    }
  } catch (err) {
    // A 404 means the project itself does not exist yet — as creatable as a
    // missing config, handled below. Anything else leaves remote state unknown.
    if (!(err instanceof SecretsApiError && err.status === 404)) {
      return {
        status: 'unchecked',
        message: err instanceof Error ? err.message : String(err),
      }
    }
  }

  try {
    await createConfig(
      DEPLOY_URL,
      token,
      target.projectName,
      target.configName,
      undefined,
      appScopedSecretsApiOptions(dir, target),
    )
    return { status: 'created' }
  } catch (err) {
    return {
      status: 'unchecked',
      message:
        `Remote config ${target.projectName}/${target.configName} was not found ` +
        `and could not be created: ${err instanceof Error ? err.message : String(err)}`,
    }
  }
}

async function bestEffortSetServerSecretsLink(dir: string, target: SecretsTarget): Promise<void> {
  try {
    const token = await ensureToken()
    await setServerSecretsLink(
      DEPLOY_URL,
      token,
      resolveSecretsAppName(dir, target.wranglerEnv),
      target.projectName,
    )
  } catch {
    // Best-effort: setup should still update local wrangler.toml when the app
    // is not deployed yet, the caller is a collaborator, or the network is down.
  }
}

async function bestEffortClearServerSecretsLink(
  dir: string,
  targetEnv: string | undefined,
): Promise<void> {
  try {
    const token = await ensureToken()
    await clearServerSecretsLink(DEPLOY_URL, token, resolveSecretsAppName(dir, targetEnv))
  } catch {
    // Best-effort for the same reasons as setup link synchronization.
  }
}

type LegacySecretsImport =
  | {
      status: 'imported'
      keys: string[]
      skipped: string[]
      requiredSecrets: string[] | null
      cleanedLegacyFile: boolean
    }
  | { status: 'config-not-empty'; existingCount: number; keys: string[] }
  | { status: 'error'; message: string }

/**
 * One-shot migration folded into `secrets setup`: apps predating the remote
 * store keep their secrets as hand-written lines in the dev-vars file (the
 * deploy pass-through). When setup links such an app and the target config
 * is EMPTY, move those values into the remote store so the next dev-run
 * rewrite of the cache can't drop them. A config that already has secrets is
 * never overwritten — the user merges manually via `secrets upload`.
 * Reserved/SDK-managed keys are not app secrets and are skipped.
 */
async function importLegacyDevVarsSecrets(
  dir: string,
  target: SecretsTarget,
  remoteCheck: LinkRemoteCheck,
): Promise<LegacySecretsImport | null> {
  if (remoteCheck.status === 'unchecked') return null
  const legacyPath = target.wranglerEnv
    ? join(dir, `.dev.vars.${target.wranglerEnv}`)
    : join(dir, '.dev.vars')
  if (!existsSync(legacyPath)) return null

  try {
    const custom = extractCustomDevVars(
      stripGeneratedSecretsCache(readFileSync(legacyPath, 'utf-8')),
    )
    if (!custom.trim()) return null
    const parsed = parseDevVars(custom)

    const secrets: Record<string, string> = {}
    const skipped: string[] = []
    for (const [key, value] of Object.entries(parsed)) {
      try {
        validateSecretName(key)
        validateSecretValue(key, value)
        secrets[key] = value
      } catch {
        skipped.push(key)
      }
    }
    const keys = Object.keys(secrets).sort()
    if (keys.length === 0) return null

    const token = await ensureToken()
    if (remoteCheck.status === 'ok') {
      const existing = await listSecrets(
        DEPLOY_URL,
        token,
        target.projectName,
        target.configName,
        appScopedSecretsApiOptions(dir, target),
      )
      if (existing.secrets.length > 0) {
        return { status: 'config-not-empty', existingCount: existing.secrets.length, keys }
      }
    }

    await uploadSecrets(
      DEPLOY_URL,
      token,
      target.projectName,
      target.configName,
      secrets,
      false,
      appScopedSecretsApiOptions(dir, target),
    )

    // The values are confirmed remote — complete the migration by deleting
    // exactly the imported keys from the legacy file so they can't shadow
    // the store or double-ship on deploy. A legacy env file with nothing
    // custom left is removed entirely (it is ignored in linked mode and
    // would only trigger the stale-file warning forever). Best-effort: on a
    // rewrite failure the leftovers are superseded by the next dev-run
    // rewrite anyway.
    let cleanedLegacyFile = false
    try {
      const cleaned = removeDevVarsKeys(
        stripGeneratedSecretsCache(readFileSync(legacyPath, 'utf-8')),
        new Set(keys),
      )
      if (target.wranglerEnv && !extractCustomDevVars(cleaned).trim()) {
        rmSync(legacyPath, { force: true })
      } else {
        writeSecretFileSync(legacyPath, cleaned ? `${cleaned}\n` : '')
      }
      cleanedLegacyFile = true
    } catch {
      // Leftover lines are harmless duplicates until the next dev run.
    }

    await refreshCacheIfLinked(token, dir, target)
    const requiredSecrets = updateRequiredSecretsIfTopLevelLinked(dir, target, keys)
    return { status: 'imported', keys, skipped: skipped.sort(), requiredSecrets, cleanedLegacyFile }
  } catch (err) {
    return { status: 'error', message: err instanceof Error ? err.message : String(err) }
  }
}

function printLegacySecretsImport(result: LegacySecretsImport | null, target: SecretsTarget): void {
  if (!result) return
  const configRef = `${target.projectName}/${target.configName}`
  if (result.status === 'imported') {
    console.log(
      `Imported ${result.keys.length} legacy secret${result.keys.length === 1 ? '' : 's'} ` +
        `from .dev.vars into ${configRef}: ${result.keys.join(', ')}`,
    )
    if (result.cleanedLegacyFile) {
      console.log('Removed the migrated lines from the local file; the remote store owns them now.')
    }
    if (result.skipped.length > 0) {
      console.log(`Skipped SDK-managed/invalid keys (left in place): ${result.skipped.join(', ')}`)
    }
    requiredSecretsNotice(result.requiredSecrets)
    return
  }
  if (result.status === 'config-not-empty') {
    console.log(
      `Found ${result.keys.length} legacy secret${result.keys.length === 1 ? '' : 's'} in .dev.vars, ` +
        `but ${configRef} already has ${result.existingCount} — not imported. ` +
        `Merge manually with npx deepspace secrets upload.`,
    )
    return
  }
  console.warn(`Warning: legacy .dev.vars secrets were not imported: ${result.message}`)
}

function printLinkRemoteWarning(check: LinkRemoteCheck): void {
  if (check.status !== 'unchecked') return
  console.warn(`Warning: Linked locally, but the remote config was not checked: ${check.message}`)
}

function positionals(args: Record<string, unknown>, firstName: string): string[] {
  const raw = Array.isArray(args._) ? args._ : []
  if (raw.length > 0) return raw.map(String)
  const first = args[firstName]
  return typeof first === 'string' && first ? [first] : []
}

type FlagDefs = Record<string, { type?: string; alias?: string | string[] }>

/**
 * Positional tokens straight from a command's raw argv.
 *
 * citty's parser claims every `-`-prefixed token as a flag, so a positional
 * value like a PEM block (`-----BEGIN …`) never reaches `args._`. Re-scan the
 * raw argv against the command's declared flags instead: declared flags (and
 * their values) are skipped, everything else is a positional — dash-prefixed
 * or not.
 */
export function rawPositionals(rawArgs: string[], argsDef: FlagDefs): string[] {
  // citty auto-registers a camelCase alias for every kebab-case flag
  // (`--no-interactive` is also accepted as `--noInteractive`), so the scan
  // must recognize both spellings or the camel form leaks in as a positional.
  const camel = (name: string) => name.replace(/-([a-z0-9])/g, (_, ch: string) => ch.toUpperCase())
  const stringFlags = new Set<string>()
  const booleanFlags = new Set<string>()
  for (const [name, def] of Object.entries(argsDef)) {
    if (def.type === 'positional') continue
    const flags = def.type === 'boolean' ? booleanFlags : stringFlags
    flags.add(name)
    flags.add(camel(name))
    for (const alias of Array.isArray(def.alias) ? def.alias : def.alias ? [def.alias] : []) {
      flags.add(alias)
      flags.add(camel(alias))
    }
  }

  const out: string[] = []
  for (let i = 0; i < rawArgs.length; i++) {
    const token = rawArgs[i]
    if (token === '--') {
      out.push(...rawArgs.slice(i + 1))
      break
    }
    if (token.length > 1 && token.startsWith('-')) {
      const body = token.startsWith('--') ? token.slice(2) : token.slice(1)
      const eq = body.indexOf('=')
      const name = eq === -1 ? body : body.slice(0, eq)
      if (booleanFlags.has(name) || (name.startsWith('no-') && booleanFlags.has(name.slice(3)))) {
        continue
      }
      if (stringFlags.has(name)) {
        if (eq === -1) i++ // the flag's value is the next token
        continue
      }
      // Not a declared flag: a value that happens to start with "-".
    }
    out.push(token)
  }
  return out
}

/**
 * True when `--<name>` appears in the raw argv (before any `--` terminator).
 *
 * citty rewrites `--no-<flag>` tokens into a `false` value for `<flag>`
 * before parsing, so a boolean literally named `no-file` can never be set
 * through `args` — the raw argv is the only place the token survives.
 */
function hasRawFlag(rawArgs: string[] | undefined, name: string): boolean {
  if (!Array.isArray(rawArgs)) return false
  for (const token of rawArgs) {
    if (token === '--') return false
    if (token === `--${name}`) return true
  }
  return false
}

function downloadFormat(value: unknown): SecretsDownloadFormat {
  const raw = typeof value === 'string' && value ? value : 'json'
  if (
    raw === 'json' ||
    raw === 'dotnet-json' ||
    raw === 'env' ||
    raw === 'yaml' ||
    raw === 'docker' ||
    raw === 'env-no-quotes'
  ) {
    return raw
  }
  throw new Error('Unsupported format. Use one of: json, dotnet-json, env, yaml, docker, env-no-quotes.')
}

export function resolveSecretsSetupArgs(args: RawArgs): {
  appDir: string
  target: SecretsTarget
  projectOverride: boolean
} {
  const dir = appDir(args)
  const project = typeof args.project === 'string' && args.project ? args.project : undefined
  const config = typeof args.config === 'string' && args.config ? args.config : undefined
  const target = resolveSecretsTarget(dir, {
    project,
    config,
    wranglerEnv: wranglerEnv(args),
  })
  return {
    appDir: dir,
    target,
    projectOverride: !!project && !!target.wranglerEnv,
  }
}

type ConfigureUnsetName = 'project' | 'config'

export function resolveConfigureUnsetArgs(args: RawArgs): {
  appDir: string
  unset: ConfigureUnsetName[]
  wranglerEnv?: string
} {
  const names = positionals(args, 'name')
  if (names.length === 0) {
    throw new Error('At least one setting is required. Use `npx deepspace secrets configure unset project config`.')
  }

  const unset: ConfigureUnsetName[] = []
  for (const name of names) {
    if (name !== 'project' && name !== 'config') {
      throw new Error(`Unsupported configure unset option "${name}". Use project and/or config.`)
    }
    if (!unset.includes(name)) unset.push(name)
  }

  return {
    appDir: appDir(args),
    unset,
    wranglerEnv: wranglerEnv(args),
  }
}

const list = defineCommand({
  meta: { name: 'list', description: 'List masked secrets in a config' },
  args: {
    ...commonArgs(),
    'only-names': { type: 'boolean', description: 'Only print secret names; omit values and metadata', default: false },
    raw: { type: 'boolean', description: 'Accepted for Doppler CLI compatibility; DeepSpace has no variable interpolation', default: false },
    type: { type: 'boolean', description: 'Accepted for Doppler CLI compatibility', default: false },
    visibility: { type: 'boolean', description: 'Accepted for Doppler CLI compatibility', default: false },
  },
  async run({ args }) {
    try {
      const token = await ensureToken()
      const { appDir: dir, target } = targetFromArgs(args)
      const result = await listSecrets(
        DEPLOY_URL,
        token,
        target.projectName,
        target.configName,
        appScopedSecretsApiOptions(dir, target),
      )
      if (args.json) {
        process.stdout.write(JSON.stringify(result, null, 2) + '\n')
        return
      }
      if (result.secrets.length === 0) {
        console.log(`No secrets in ${target.projectName}/${target.configName}.`)
        return
      }
      if (args.onlyNames) {
        for (const item of result.secrets) console.log(item.key)
        return
      }
      for (const item of result.secrets) {
        console.log(`${item.key.padEnd(32)} ${item.masked}  v${item.version}  ${item.updatedAt}`)
      }
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

const SET_ARGS = {
  ...commonArgs(),
  secret: { type: 'positional', description: 'KEY=value or KEY value', required: true },
  'no-interactive': { type: 'boolean', description: 'Accepted for Doppler CLI compatibility', default: false },
  raw: { type: 'boolean', description: 'Accepted for Doppler CLI compatibility; DeepSpace has no variable interpolation', default: false },
  type: { type: 'string', description: 'Accepted for Doppler CLI compatibility', required: false },
  visibility: { type: 'string', description: 'Accepted for Doppler CLI compatibility', required: false },
} as const

const set = defineCommand({
  meta: { name: 'set', description: 'Set remote secrets from KEY=value or KEY value' },
  args: SET_ARGS,
  async run({ args, rawArgs }) {
    try {
      const token = await ensureToken()
      const { appDir: dir, target } = targetFromArgs(args)
      // Values may start with "-" (PEM blocks, negative numbers), which the
      // arg parser would swallow as flags — recover them from the raw argv.
      const inputs = Array.isArray(rawArgs)
        ? rawPositionals(rawArgs, SET_ARGS)
        : positionals(args, 'secret')
      const secrets = parseSetInputs(inputs)
      await uploadSecrets(
        DEPLOY_URL,
        token,
        target.projectName,
        target.configName,
        secrets,
        false,
        appScopedSecretsApiOptions(dir, target),
      )
      const count = await refreshCacheIfLinked(token, dir, target)
      const keys = Object.keys(secrets).sort()
      const requiredSecrets = updateRequiredSecretsIfTopLevelLinked(dir, target, keys)
      if (args.json) {
        process.stdout.write(JSON.stringify({ ok: true, keys, target, ...cacheJson(count), ...requiredSecretsJson(requiredSecrets) }) + '\n')
        return
      }
      console.log(`Set ${keys.length} secret${keys.length === 1 ? '' : 's'} in ${target.projectName}/${target.configName}.`)
      requiredSecretsNotice(requiredSecrets)
      if (count === null) {
        unlinkedTargetNotice(target)
      } else {
        console.log(`Refreshed ${count} cached secret${count === 1 ? '' : 's'}.`)
        redeployReminder(target)
      }
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

const get = defineCommand({
  meta: { name: 'get', description: 'Get one or more secrets; pass --plain to print values' },
  args: {
    ...commonArgs(),
    key: { type: 'positional', description: 'Secret key', required: true },
    plain: { type: 'boolean', description: 'Print plaintext value', default: false },
    'no-exit-on-missing-secret': { type: 'boolean', description: 'Do not exit when a requested secret is missing', default: false },
    raw: { type: 'boolean', description: 'Accepted for Doppler CLI compatibility; DeepSpace has no variable interpolation', default: false },
    copy: { type: 'boolean', description: 'Accepted for Doppler CLI compatibility; clipboard copy is not implemented', default: false },
    type: { type: 'boolean', description: 'Accepted for Doppler CLI compatibility', default: false },
    visibility: { type: 'boolean', description: 'Accepted for Doppler CLI compatibility', default: false },
  },
  async run({ args }) {
    try {
      const token = await ensureToken()
      const { appDir: dir, target } = targetFromArgs(args)
      const keys = positionals(args, 'key').map((key) => validateSecretName(key))
      if (args.plain) {
        const values: Record<string, string> = {}
        const missing: string[] = []
        for (const key of keys) {
          try {
            values[key] = await getSecretPlain(
              DEPLOY_URL,
              token,
              target.projectName,
              target.configName,
              key,
              appScopedSecretsApiOptions(dir, target),
            )
          } catch (err) {
            if (!args.noExitOnMissingSecret) throw err
            missing.push(key)
          }
        }
        if (args.json) {
          process.stdout.write(JSON.stringify({ secrets: values, missing }, null, 2) + '\n')
          return
        }
        for (const key of keys) if (Object.prototype.hasOwnProperty.call(values, key)) process.stdout.write(values[key] + '\n')
        return
      }
      const result = await listSecrets(
        DEPLOY_URL,
        token,
        target.projectName,
        target.configName,
        appScopedSecretsApiOptions(dir, target),
      )
      const found = result.secrets.filter((item) => keys.includes(item.key))
      const foundKeys = new Set(found.map((item) => item.key))
      const missing = keys.filter((key) => !foundKeys.has(key))
      if (missing.length > 0 && !args.noExitOnMissingSecret) {
        throw new Error(`Secret "${missing[0]}" not found in ${target.projectName}/${target.configName}.`)
      }
      if (args.json) {
        process.stdout.write(JSON.stringify({ secrets: found, missing }, null, 2) + '\n')
        return
      }
      for (const item of found) process.stdout.write(`${item.key} ${item.masked} v${item.version}\n`)
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

const del = defineCommand({
  meta: { name: 'delete', description: 'Delete one or more remote secrets' },
  args: {
    ...commonArgs(),
    key: { type: 'positional', description: 'Secret key', required: true },
    yes: { type: 'boolean', alias: 'y', description: 'Proceed without confirmation; accepted for Doppler CLI compatibility', default: false },
    raw: { type: 'boolean', description: 'Accepted for Doppler CLI compatibility; DeepSpace has no variable interpolation', default: false },
  },
  async run({ args }) {
    try {
      const token = await ensureToken()
      const { appDir: dir, target } = targetFromArgs(args)
      const keys = positionals(args, 'key').map((key) => validateSecretName(key))
      for (const key of keys) {
        await deleteSecret(
          DEPLOY_URL,
          token,
          target.projectName,
          target.configName,
          key,
          appScopedSecretsApiOptions(dir, target),
        )
      }
      const count = await refreshCacheIfLinked(token, dir, target)
      const requiredSecrets = removeRequiredSecretsIfTopLevelLinked(dir, target, keys)
      if (args.json) {
        process.stdout.write(JSON.stringify({ ok: true, deleted: keys, target, ...cacheJson(count), ...requiredSecretsJson(requiredSecrets) }) + '\n')
        return
      }
      console.log(`Deleted ${keys.length} secret${keys.length === 1 ? '' : 's'} from ${target.projectName}/${target.configName}.`)
      requiredSecretsNotice(requiredSecrets)
      if (count === null) {
        unlinkedTargetNotice(target)
      } else {
        console.log(`Refreshed ${count} cached secret${count === 1 ? '' : 's'}.`)
        redeployReminder(target)
      }
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

const pull = defineCommand({
  meta: { name: 'pull', description: 'Refresh .dev.vars* only when the selected config is linked' },
  args: commonArgs(),
  async run({ args }) {
    try {
      const token = await ensureToken()
      const { appDir: dir, target } = targetFromArgs(args)
      const values = await fetchSecretsValues(
        DEPLOY_URL,
        token,
        target.projectName,
        target.configName,
        appScopedSecretsApiOptions(dir, target),
      )
      const count = Object.keys(values).length
      const linked = secretsTargetMatchesLink(dir, target)
      if (linked) writeSecretsCacheFile(dir, values, target)
      if (args.json) {
        process.stdout.write(JSON.stringify({ ok: true, target, linked, cachedSecrets: linked ? count : null }) + '\n')
        return
      }
      console.log(`${linked ? 'Pulled' : 'Read'} ${count} secret${count === 1 ? '' : 's'} from ${target.projectName}/${target.configName}.`)
      if (!linked) unlinkedTargetNotice(target)
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

const download = defineCommand({
  meta: { name: 'download', description: 'Download a config\'s secrets in a Doppler-compatible format' },
  args: {
    ...commonArgs(),
    file: { type: 'positional', description: 'Output filepath; omit when using --no-file', required: false },
    format: { type: 'string', description: 'Output format: json, dotnet-json, env, yaml, docker, env-no-quotes', required: false },
    'no-file': { type: 'boolean', description: 'Print secrets to stdout instead of writing a file', default: false },
    raw: { type: 'boolean', description: 'Accepted for Doppler CLI compatibility; DeepSpace has no variable interpolation', default: false },
    'no-cache': { type: 'boolean', description: 'Accepted for Doppler CLI compatibility; DeepSpace does not use a download fallback cache', default: false },
    'no-fallback': { type: 'boolean', description: 'Accepted for Doppler CLI compatibility; DeepSpace does not use fallback files', default: false },
    offline: { type: 'boolean', description: 'Accepted for Doppler CLI compatibility; offline fallback files are not supported', default: false },
  },
  async run({ args, rawArgs }) {
    try {
      const token = await ensureToken()
      const { appDir: dir, target } = targetFromArgs(args)
      const values = await fetchSecretsValues(
        DEPLOY_URL,
        token,
        target.projectName,
        target.configName,
        appScopedSecretsApiOptions(dir, target),
      )
      const output = formatSecretsDownload(values, downloadFormat(args.format))
      const file = typeof args.file === 'string' && args.file ? args.file : undefined
      const noFile = Boolean(args.noFile) || hasRawFlag(rawArgs, 'no-file')
      if (noFile || !file) {
        if (!noFile && !file) {
          throw new Error('Provide an output filepath or pass --no-file to print to stdout.')
        }
        process.stdout.write(output)
        return
      }
      writeSecretFileSync(resolve(file), output)
      if (args.json) {
        process.stdout.write(JSON.stringify({ ok: true, path: resolve(file), target, secretCount: Object.keys(values).length }) + '\n')
        return
      }
      console.log(`Downloaded ${Object.keys(values).length} secret${Object.keys(values).length === 1 ? '' : 's'} to ${file}.`)
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

const upload = defineCommand({
  meta: { name: 'upload', description: 'Upload remote secrets from a dotenv or JSON file' },
  args: {
    ...commonArgs(),
    file: { type: 'positional', description: 'Path to dotenv/JSON file', required: true },
    replace: { type: 'boolean', description: 'Delete secrets absent from the upload', default: false },
    raw: { type: 'boolean', description: 'Accepted for Doppler CLI compatibility; DeepSpace has no variable interpolation', default: false },
  },
  async run({ args }) {
    try {
      const token = await ensureToken()
      const { appDir: dir, target } = targetFromArgs(args)
      const file = String(args.file)
      const content = readExistingFile(file)
      const secrets = parseSecretsUpload(content)
      await uploadSecrets(
        DEPLOY_URL,
        token,
        target.projectName,
        target.configName,
        secrets,
        !!args.replace,
        appScopedSecretsApiOptions(dir, target),
      )
      const count = await refreshCacheIfLinked(token, dir, target)
      const requiredSecrets = updateRequiredSecretsIfTopLevelLinked(dir, target, Object.keys(secrets), {
        replace: !!args.replace,
      })
      if (args.json) {
        process.stdout.write(JSON.stringify({ ok: true, uploaded: Object.keys(secrets), target, ...cacheJson(count), ...requiredSecretsJson(requiredSecrets) }) + '\n')
        return
      }
      console.log(`Uploaded ${Object.keys(secrets).length} secret${Object.keys(secrets).length === 1 ? '' : 's'} to ${target.projectName}/${target.configName}.`)
      requiredSecretsNotice(requiredSecrets)
      if (count === null) {
        unlinkedTargetNotice(target)
      } else {
        console.log(`Refreshed ${count} cached secret${count === 1 ? '' : 's'}.`)
        redeployReminder(target)
      }
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

const configsList = defineCommand({
  meta: { name: 'list', description: 'List configs in a secrets project' },
  args: commonArgs(),
  async run({ args }) {
    try {
      const token = await ensureToken()
      const { appDir: dir, target } = targetFromArgs(args)
      const configs = await listConfigs(
        DEPLOY_URL,
        token,
        target.projectName,
        appScopedSecretsApiOptions(dir, target),
      )
      if (args.json) {
        process.stdout.write(JSON.stringify({ projectName: target.projectName, configs }, null, 2) + '\n')
        return
      }
      if (configs.length === 0) {
        console.log(`No configs in ${target.projectName}.`)
        return
      }
      for (const cfg of configs) {
        console.log(`${cfg.name.padEnd(24)} ${cfg.secretCount ?? 0} secret${cfg.secretCount === 1 ? '' : 's'}  ${cfg.updatedAt}`)
      }
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

const configsCreate = defineCommand({
  meta: { name: 'create', description: 'Create a remote config without linking it' },
  args: {
    ...commonArgs(),
    configName: { type: 'positional', description: 'Config name', required: false },
    name: { type: 'string', description: 'Config name; Doppler-compatible --name flag', required: false },
  },
  async run({ args }) {
    try {
      if (args.copyFrom !== undefined || args['copy-from'] !== undefined) {
        throw new Error('Unsupported flag: --copy-from. Use `npx deepspace secrets configs clone <source-config> --name <new-config>`.')
      }
      const token = await ensureToken()
      const dir = appDir(args)
      const projectName = validateProjectName(args.project || resolveSecretsTarget(dir, { wranglerEnv: wranglerEnv(args) }).projectName)
      const rawName = typeof args.config === 'string' && args.config
        ? args.config
        : typeof args.configName === 'string' && args.configName
          ? args.configName
          : args.name
      if (typeof rawName !== 'string' || !rawName) throw new Error('Config name required')
      const configName = validateConfigName(rawName)
      const target = { projectName, configName, wranglerEnv: wranglerEnv(args) }
      const result = await createConfig(
        DEPLOY_URL,
        token,
        projectName,
        configName,
        undefined,
        appScopedSecretsApiOptions(dir, target),
      )
      const count = await refreshCacheIfLinked(token, dir, target)
      if (args.json) {
        process.stdout.write(JSON.stringify({ ...result, target, ...cacheJson(count) }) + '\n')
        return
      }
      if (result.created) {
        console.log(`Created ${projectName}/${configName}.`)
      } else {
        console.log(`Config ${projectName}/${configName} already exists.`)
      }
      if (count === null) {
        unlinkedTargetNotice(target)
      } else {
        console.log(`Refreshed ${count} cached secret${count === 1 ? '' : 's'}.`)
      }
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

export function resolveConfigCloneArgs(args: RawArgs): {
  appDir: string
  sourceConfigName: string
  target: SecretsTarget
} {
  const dir = appDir(args)
  const baseTarget = resolveSecretsTarget(dir, {
    project: typeof args.project === 'string' && args.project ? args.project : undefined,
    wranglerEnv: wranglerEnv(args),
  })
  const sources = positionals(args, 'sourceConfig')
  if (sources.length > 1) throw new Error('Only one source config can be provided.')
  const sourceConfigName = validateConfigName(
    sources[0] ?? (typeof args.config === 'string' && args.config ? args.config : baseTarget.configName),
  )
  if (typeof args.name !== 'string' || !args.name) {
    throw new Error('Destination config name required; pass --name <new-config>.')
  }
  const target = {
    projectName: baseTarget.projectName,
    configName: validateConfigName(args.name),
    wranglerEnv: baseTarget.wranglerEnv,
  }
  return { appDir: dir, sourceConfigName, target }
}

const configsClone = defineCommand({
  meta: { name: 'clone', description: 'Create a remote config by copying another config server-side' },
  args: {
    ...commonArgs(),
    sourceConfig: { type: 'positional', description: 'Source config name (default: --config or linked/default config)', required: false },
    name: { type: 'string', description: 'Destination config name', required: true },
  },
  async run({ args }) {
    try {
      const token = await ensureToken()
      const { appDir: dir, sourceConfigName, target } = resolveConfigCloneArgs(args)
      const result = await createConfig(
        DEPLOY_URL,
        token,
        target.projectName,
        target.configName,
        sourceConfigName,
        appScopedSecretsApiOptions(dir, target),
      )
      const count = await refreshCacheIfLinked(token, dir, target)
      if (args.json) {
        process.stdout.write(JSON.stringify({ ...result, target, sourceConfigName, ...cacheJson(count) }) + '\n')
        return
      }
      if (result.created) {
        const copiedSecrets = result.copiedSecrets ?? 0
        console.log(
          `Created ${target.projectName}/${target.configName} from ${sourceConfigName} ` +
            `(${copiedSecrets} secret${copiedSecrets === 1 ? '' : 's'}).`,
        )
      } else {
        console.log(`Config ${target.projectName}/${target.configName} already exists.`)
      }
      if (count === null) {
        unlinkedTargetNotice(target)
      } else {
        console.log(`Refreshed ${count} cached secret${count === 1 ? '' : 's'}.`)
      }
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

const configsDelete = defineCommand({
  meta: { name: 'delete', description: 'Delete a config and all of its secrets' },
  args: {
    ...commonArgs(),
    name: { type: 'positional', description: 'Config name', required: false },
  },
  async run({ args }) {
    try {
      const token = await ensureToken()
      const dir = appDir(args)
      const { target } = targetFromArgs(args)
      const rawName = typeof args.name === 'string' && args.name ? args.name : args.config
      if (typeof rawName !== 'string' || !rawName) throw new Error('Config name required')
      const configName = validateConfigName(rawName)
      const link = resolveSecretsLink(dir, target.wranglerEnv)
      const deletingLinked = link.linked &&
        link.projectName === target.projectName &&
        link.configName === configName
      await deleteConfig(
        DEPLOY_URL,
        token,
        target.projectName,
        configName,
        appScopedSecretsApiOptions(dir, target),
      )
      if (deletingLinked) {
        removeWranglerSecretsLink(dir, target.wranglerEnv)
        clearSecretsCacheFile(dir)
      }
      if (args.json) {
        process.stdout.write(JSON.stringify({
          ok: true,
          deleted: configName,
          projectName: target.projectName,
          unlinked: deletingLinked,
          cachedSecrets: deletingLinked ? 0 : null,
        }) + '\n')
        return
      }
      console.log(`Deleted ${target.projectName}/${configName}.`)
      if (deletingLinked) {
        console.log('Deleted config was linked; removed the local link and cleared generated cache.')
        redeployReminder({ ...target, configName })
      }
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

const setup = defineCommand({
  meta: { name: 'setup', description: 'Link this app/env to a secrets project config' },
  args: setupArgs(),
  async run({ args }) {
    try {
      const { appDir: dir, target, projectOverride } = resolveSecretsSetupArgs(args)
      writeLinkForTarget(dir, target, projectOverride)
      await bestEffortSetServerSecretsLink(dir, target)
      const remoteCheck = await ensureLinkedRemoteTarget(dir, target)
      const migration = await importLegacyDevVarsSecrets(dir, target, remoteCheck)
      if (args.json) {
        process.stdout.write(
          JSON.stringify({ ok: true, target, remoteCheck, ...(migration && { migration }) }) + '\n',
        )
        return
      }
      console.log(`Linked ${target.wranglerEnv ? `[env.${target.wranglerEnv}]` : 'top-level app'} to ${target.projectName}/${target.configName}.`)
      if (remoteCheck.status === 'created') {
        console.log(`Created remote config ${target.projectName}/${target.configName}.`)
      }
      printLegacySecretsImport(migration, target)
      printLinkRemoteWarning(remoteCheck)
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

const configureUnset = defineCommand({
  meta: { name: 'unset', description: 'Unset linked secrets project/config settings' },
  args: {
    ...scopedArgs(),
    name: { type: 'positional', description: 'Setting names to unset: project and/or config', required: false },
  },
  async run({ args }) {
    try {
      const { appDir: dir, unset, wranglerEnv: targetEnv } = resolveConfigureUnsetArgs(args)
      removeWranglerSecretsLinkKeys(dir, targetEnv, unset)
      clearSecretsCacheFile(dir)
      if (unset.includes('project')) {
        await bestEffortClearServerSecretsLink(dir, targetEnv)
      }
      if (args.json) {
        process.stdout.write(JSON.stringify({ ok: true, unset, env: targetEnv ?? null, cachedSecrets: 0 }) + '\n')
        return
      }
      console.log(`Unset ${unset.join(', ')} for ${targetEnv ? `[env.${targetEnv}]` : 'top-level app'} secrets config.`)
      console.log('Cleared generated secrets cache for that target.')
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err))
      process.exit(1)
    }
  },
})

const configure = defineCommand({
  meta: { name: 'configure', description: 'Manage local secrets CLI configuration' },
  subCommands: {
    unset: configureUnset,
  },
})

const configs = defineCommand({
  meta: { name: 'configs', description: 'Manage remote secrets configs' },
  subCommands: {
    list: configsList,
    create: configsCreate,
    clone: configsClone,
    delete: configsDelete,
  },
})

export default defineCommand({
  meta: { name: 'secrets', description: 'Manage remote DeepSpace app secrets' },
  subCommands: {
    list,
    set,
    get,
    delete: del,
    pull,
    download,
    upload,
    setup,
    configure,
    configs,
  },
})

export function parseSetInputs(inputs: string[]): Record<string, string> {
  if (inputs.length === 0) throw new Error('At least one secret is required')
  const out: Record<string, string> = {}

  if (inputs.every((input) => input.includes('='))) {
    for (const raw of inputs) {
      const eq = raw.indexOf('=')
      const key = validateSecretName(raw.slice(0, eq))
      const value = raw.slice(eq + 1)
      validateSecretValue(key, value)
      out[key] = value
    }
    return out
  }

  if (inputs.length === 1) {
    const raw = inputs[0]
    throw new Error(`Secret value required for "${raw}". Use KEY=value or KEY value.`)
  }

  if (inputs.length === 2 && !inputs[0].includes('=')) {
    const key = validateSecretName(inputs[0])
    const value = inputs[1]
    validateSecretValue(key, value)
    out[key] = value
    return out
  }

  throw new Error('Use KEY=value for multiple secrets, or KEY value for one secret.')
}

function readExistingFile(path: string): string {
  const full = resolve(path)
  if (!existsSync(full)) throw new Error(`File not found: ${path}`)
  return readFileSync(full, 'utf-8')
}
